/**
 * QuestionBankPage — Main container for the LPA Question Bank.
 *
 * Renders search, tag filter chips, active/inactive toggle, the question
 * list (QuestionCard), and orchestrates create/edit/delete flows via
 * QuestionFormDialog.
 */

import { useState, useMemo, useCallback } from 'react';
import { useLanguage } from '@/core/LanguageContext';
import { useQuestions, useCreateQuestion, useUpdateQuestion, useDeleteQuestion } from '../hooks/useQuestions';
import { useTags } from '../hooks/useTags';
import type { LpaQuestionWithTags, LpaTag } from '../types';
import QuestionCard from './QuestionCard';
import QuestionFormDialog from './QuestionFormDialog';
import TagManager from './TagManager';
import { Button } from '@/shared/ui/button';
import { Input } from '@/shared/ui/input';
import { Badge } from '@/shared/ui/badge';
import { Skeleton } from '@/shared/ui/skeleton';
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from '@/shared/ui/collapsible';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/shared/ui/alert-dialog';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuRadioGroup,
  DropdownMenuRadioItem,
  DropdownMenuTrigger,
} from '@/shared/ui/dropdown-menu';
import { Plus, Search, Filter, HelpCircle, Tag, ChevronDown } from 'lucide-react';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

type StatusFilter = 'all' | 'active' | 'inactive';

// ---------------------------------------------------------------------------
// Tag filter bar
// ---------------------------------------------------------------------------

function TagFilterBar({
  tags,
  selectedTagIds,
  onToggle,
  onClear,
}: {
  tags: LpaTag[];
  selectedTagIds: string[];
  onToggle: (id: string) => void;
  onClear: () => void;
}) {
  const { t, getTranslation } = useLanguage();
  const activeTags = tags.filter((tag) => tag.is_active);

  if (activeTags.length === 0) return null;

  return (
    <div className="flex flex-wrap items-center gap-1.5">
      <span className="text-xs text-muted-foreground font-medium mr-1">
        <Filter className="h-3 w-3 inline-block mr-1" />
        {t('mod_lpa.questions.filterByTag', 'Tags:')}
      </span>
      {activeTags.map((tag) => {
        const selected = selectedTagIds.includes(tag.id);
        return (
          <button key={tag.id} type="button" onClick={() => onToggle(tag.id)}>
            <Badge
              variant={selected ? 'default' : 'outline'}
              className="cursor-pointer text-xs"
              style={
                tag.color
                  ? selected
                    ? { backgroundColor: tag.color, borderColor: tag.color, color: '#fff' }
                    : { borderColor: tag.color, color: tag.color }
                  : undefined
              }
            >
              {getTranslation(tag.name_translations, tag.name)}
            </Badge>
          </button>
        );
      })}
      {selectedTagIds.length > 0 && (
        <button
          type="button"
          onClick={onClear}
          className="text-xs text-muted-foreground underline hover:text-foreground ml-1"
        >
          {t('mod_lpa.questions.clearFilters', 'Clear')}
        </button>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Empty state
// ---------------------------------------------------------------------------

function EmptyState({ hasFilters, t }: { hasFilters: boolean; t: (key: string, fallback: string) => string }) {
  return (
    <div className="flex flex-col items-center justify-center py-16 text-center">
      <HelpCircle className="h-12 w-12 text-muted-foreground mb-4" />
      <h3 className="text-lg font-medium mb-1">
        {hasFilters
          ? t('mod_lpa.questions.noResults', 'No questions match your filters')
          : t('mod_lpa.questions.emptyTitle', 'No questions yet')}
      </h3>
      <p className="text-sm text-muted-foreground max-w-sm">
        {hasFilters
          ? t('mod_lpa.questions.noResultsHint', 'Try adjusting your search or tag filters.')
          : t('mod_lpa.questions.emptyHint', 'Create your first conformance question to start building your audit question bank.')}
      </p>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Loading skeleton
// ---------------------------------------------------------------------------

function LoadingSkeleton() {
  return (
    <div className="space-y-3">
      {Array.from({ length: 4 }).map((_, i) => (
        <Skeleton key={i} className="h-20 w-full rounded-lg" />
      ))}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Main component
// ---------------------------------------------------------------------------

export default function QuestionBankPage() {
  const { t } = useLanguage();

  // Data
  const { data: questions, isLoading: questionsLoading } = useQuestions();
  const { data: tags } = useTags();
  const createQuestion = useCreateQuestion();
  const updateQuestion = useUpdateQuestion();
  const deleteQuestionMutation = useDeleteQuestion();

  // UI state
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedTagIds, setSelectedTagIds] = useState<string[]>([]);
  const [statusFilter, setStatusFilter] = useState<StatusFilter>('active');
  const [formOpen, setFormOpen] = useState(false);
  const [editingQuestion, setEditingQuestion] = useState<LpaQuestionWithTags | null>(null);
  const [deletingQuestion, setDeletingQuestion] = useState<LpaQuestionWithTags | null>(null);
  const [tagsOpen, setTagsOpen] = useState(false);

  // ------- Tag filter helpers -------
  const toggleTagFilter = useCallback((id: string) => {
    setSelectedTagIds((prev) =>
      prev.includes(id) ? prev.filter((i) => i !== id) : [...prev, id],
    );
  }, []);

  const clearTagFilter = useCallback(() => setSelectedTagIds([]), []);

  // ------- Filtered list -------
  const filteredQuestions = useMemo(() => {
    if (!questions) return [];

    return questions.filter((q) => {
      // Status filter
      if (statusFilter === 'active' && !q.is_active) return false;
      if (statusFilter === 'inactive' && q.is_active) return false;

      // Tag filter (AND: question must have ALL selected tags)
      if (selectedTagIds.length > 0) {
        const qTagIds = new Set(q.tags.map((t) => t.id));
        if (!selectedTagIds.every((id) => qTagIds.has(id))) return false;
      }

      // Text search (case-insensitive on question text)
      if (searchQuery.trim()) {
        const lowerQ = searchQuery.toLowerCase();
        const matchesQuestion = q.question.toLowerCase().includes(lowerQ);
        const matchesExplanation = q.explanation?.toLowerCase().includes(lowerQ);
        const matchesReaction = q.reaction_plan?.toLowerCase().includes(lowerQ);
        if (!matchesQuestion && !matchesExplanation && !matchesReaction) return false;
      }

      return true;
    });
  }, [questions, searchQuery, selectedTagIds, statusFilter]);

  const hasFilters = searchQuery.trim() !== '' || selectedTagIds.length > 0 || statusFilter !== 'all';

  // ------- Dialog handlers -------
  const handleOpenCreate = () => {
    setEditingQuestion(null);
    setFormOpen(true);
  };

  const handleOpenEdit = (q: LpaQuestionWithTags) => {
    setEditingQuestion(q);
    setFormOpen(true);
  };

  const handleFormSubmit = (data: {
    question: string;
    question_translations: Record<string, string>;
    explanation: string | null;
    explanation_translations: Record<string, string>;
    reaction_plan: string | null;
    reaction_plan_translations: Record<string, string>;
    estimated_duration_seconds: number | null;
    is_active: boolean;
    tag_ids: string[];
  }) => {
    if (editingQuestion) {
      updateQuestion.mutate(
        { questionId: editingQuestion.id, input: data },
        { onSuccess: () => setFormOpen(false) },
      );
    } else {
      createQuestion.mutate(data, { onSuccess: () => setFormOpen(false) });
    }
  };

  const handleConfirmDelete = () => {
    if (!deletingQuestion) return;
    deleteQuestionMutation.mutate(deletingQuestion.id, {
      onSuccess: () => setDeletingQuestion(null),
    });
  };

  // ------- Counts -------
  const totalCount = questions?.length ?? 0;
  const activeCount = questions?.filter((q) => q.is_active).length ?? 0;
  const inactiveCount = totalCount - activeCount;

  return (
    <>
      <div className="space-y-4">
        {/* Toolbar row: search + manage tags + status filter + create button */}
        <div className="flex flex-col sm:flex-row gap-3">
          {/* Search */}
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
            <Input
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder={t('mod_lpa.questions.searchPlaceholder', 'Search questions...')}
              className="pl-9"
            />
          </div>

          {/* Manage Tags toggle */}
          <Button
            variant={tagsOpen ? 'secondary' : 'outline'}
            onClick={() => setTagsOpen((prev) => !prev)}
            className="gap-2"
          >
            <Tag className="h-4 w-4" />
            {t('mod_lpa.questions.manageTags', 'Manage Tags')}
            <ChevronDown className={`h-3.5 w-3.5 transition-transform ${tagsOpen ? 'rotate-180' : ''}`} />
          </Button>

          {/* Status filter */}
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="outline" className="min-w-[130px] justify-between gap-2">
                <Filter className="h-4 w-4" />
                {statusFilter === 'all' && t('mod_lpa.questions.statusAll', `All (${totalCount})`)}
                {statusFilter === 'active' && t('mod_lpa.questions.statusActive', `Active (${activeCount})`)}
                {statusFilter === 'inactive' && t('mod_lpa.questions.statusInactive', `Inactive (${inactiveCount})`)}
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              <DropdownMenuRadioGroup value={statusFilter} onValueChange={(v) => setStatusFilter(v as StatusFilter)}>
                <DropdownMenuRadioItem value="all">
                  {t('mod_lpa.questions.statusAll', `All (${totalCount})`)}
                </DropdownMenuRadioItem>
                <DropdownMenuRadioItem value="active">
                  {t('mod_lpa.questions.statusActive', `Active (${activeCount})`)}
                </DropdownMenuRadioItem>
                <DropdownMenuRadioItem value="inactive">
                  {t('mod_lpa.questions.statusInactive', `Inactive (${inactiveCount})`)}
                </DropdownMenuRadioItem>
              </DropdownMenuRadioGroup>
            </DropdownMenuContent>
          </DropdownMenu>

          {/* Create button */}
          <Button onClick={handleOpenCreate} className="gap-2">
            <Plus className="h-4 w-4" />
            {t('mod_lpa.questions.addQuestion', 'Add Question')}
          </Button>
        </div>

        {/* Collapsible Tag Manager panel */}
        <Collapsible open={tagsOpen} onOpenChange={setTagsOpen}>
          <CollapsibleContent className="data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0">
            <TagManager />
          </CollapsibleContent>
        </Collapsible>

        {/* Tag filter chips */}
        {tags && tags.length > 0 && (
          <TagFilterBar
            tags={tags}
            selectedTagIds={selectedTagIds}
            onToggle={toggleTagFilter}
            onClear={clearTagFilter}
          />
        )}

        {/* Question list */}
        {questionsLoading ? (
          <LoadingSkeleton />
        ) : filteredQuestions.length === 0 ? (
          <EmptyState hasFilters={hasFilters} t={t} />
        ) : (
          <div className="space-y-2">
            {filteredQuestions.map((q) => (
              <QuestionCard
                key={q.id}
                question={q}
                onEdit={handleOpenEdit}
                onDelete={setDeletingQuestion}
              />
            ))}
          </div>
        )}

        {/* Result count */}
        {!questionsLoading && filteredQuestions.length > 0 && (
          <p className="text-xs text-muted-foreground text-center">
            {t('mod_lpa.questions.showing', `Showing ${filteredQuestions.length} of ${totalCount} questions`)}
          </p>
        )}
      </div>

      {/* Create / Edit dialog */}
      <QuestionFormDialog
        open={formOpen}
        onOpenChange={setFormOpen}
        question={editingQuestion}
        onSubmit={handleFormSubmit}
        isSubmitting={createQuestion.isPending || updateQuestion.isPending}
      />

      {/* Delete confirmation */}
      <AlertDialog open={!!deletingQuestion} onOpenChange={() => setDeletingQuestion(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>
              {t('mod_lpa.questions.deleteTitle', 'Delete question?')}
            </AlertDialogTitle>
            <AlertDialogDescription>
              {t(
                'mod_lpa.questions.deleteDescription',
                'This will permanently delete this question and remove it from all audits. This action cannot be undone.',
              )}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>
              {t('mod_lpa.questions.cancel', 'Cancel')}
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={handleConfirmDelete}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              {t('mod_lpa.questions.confirmDelete', 'Delete')}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}
